import { Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { catchError, EMPTY, from, Observable, tap, throwError } from 'rxjs';
import { ColumnMapping } from '../components-openagri/agx-pivot-table/agx-pivot-table.component';

/** Buffer type returned by exceljs `workbook.xlsx.writeBuffer()`. */
export type ExcelWriteBuffer = Awaited<
  ReturnType<Workbook['xlsx']['writeBuffer']>
>;

export abstract class ExcelHelper {
  static downloadWorkbook$(
    workbook: Workbook,
    fileName: string,
  ): Observable<ExcelWriteBuffer> {
    return from(workbook.xlsx.writeBuffer()).pipe(
      tap((buffer: ExcelWriteBuffer) => {
        const blob = new Blob([buffer as BlobPart], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(
          blob,
          fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`,
        );
      }),
      catchError((error: unknown) => {
        return throwError(() => this._toError(error));
      }),
    );
  }

  static addAoASheet(
    workbook: Workbook,
    name: string,
    rows: unknown[][] | undefined,
  ): Worksheet {
    const sheet = workbook.addWorksheet(name);
    if (rows?.length) {
      sheet.addRows(rows);
    }
    return sheet;
  }

  /**
   * Flattens an HTML table into rows of cell text, expanding `rowspan` and `colspan` so every row
   * keeps the same column order. A row that omits a spanned cell would otherwise shift its
   * remaining values into the wrong columns. Cells covered by a span repeat the spanning text.
   */
  static tableToRows(table: HTMLTableElement): string[][] {
    const rows: string[][] = [];
    const tableRows: HTMLTableRowElement[] = Array.from(table.rows);

    tableRows.forEach((tableRow: HTMLTableRowElement, rowIndex: number) => {
      const row: string[] = (rows[rowIndex] ??= []);
      let columnIndex = 0;

      Array.from(tableRow.cells).forEach((cell) => {
        // skip columns already filled by a rowspan from an earlier row
        while (row[columnIndex] !== undefined) {
          columnIndex++;
        }

        const text: string = (cell.textContent ?? '').trim();
        const rowSpan: number = Math.min(
          Math.max(cell.rowSpan, 1),
          tableRows.length - rowIndex,
        );
        const colSpan: number = Math.max(cell.colSpan, 1);

        for (let spannedRow = 0; spannedRow < rowSpan; spannedRow++) {
          const target: string[] = (rows[rowIndex + spannedRow] ??= []);
          for (let spannedCol = 0; spannedCol < colSpan; spannedCol++) {
            target[columnIndex + spannedCol] = text;
          }
        }

        columnIndex += colSpan;
      });
    });

    return rows.map((row: string[]) => Array.from(row, (cell) => cell ?? ''));
  }

  static downloadPivotTableData$(
    title: string,
    data: any[],
    selectedColumns: ColumnMapping[],
    aggregatedColumns: ColumnMapping[],
    includeRawData = false,
  ): Observable<ExcelWriteBuffer> {
    if (data.length === 0) {
      return EMPTY;
    }

    try {
      const workbook: Workbook = new Workbook();
      this.addAoASheet(
        workbook,
        'Sheet1',
        this.dataToRows(data, selectedColumns, aggregatedColumns),
      );

      if (includeRawData) {
        this.addRawData(data, workbook);
      }

      return this.downloadWorkbook$(
        workbook,
        title + '-' + this.dateTime() + '.xlsx',
      );
    } catch (error: unknown) {
      return throwError(() => this._toError(error));
    }
  }

  private static _toError(error: unknown): Error {
    return error instanceof Error
      ? error
      : new Error('Failed to generate Excel file');
  }

  private static addRawData(data: any[], workbook: Workbook) {
    const keys = Object.keys(data[0]);
    // only save non-null non-object properties
    const finalData = data.map((obj) => this.filterObjectProperties(obj, keys));
    const headers = Object.keys(finalData[0] ?? {});
    const rows = [
      headers,
      ...finalData.map((obj) => headers.map((key) => obj[key])),
    ];
    this.addAoASheet(workbook, 'Sheet2', rows);
  }

  private static filterObjectProperties(obj, keys) {
    return keys.reduce((acc, key) => {
      const value = obj[key];
      if (typeof value !== 'object' && value && key != '__typename') {
        return { ...acc, [key]: value };
      } else {
        return acc;
      }
    }, {});
  }

  static dataToRows(
    data,
    selectedColumns,
    aggregatedColumns,
  ): Array<Array<any>> {
    const rows = data.map((dataRow) =>
      selectedColumns
        .map((mapping) => {
          let res = dataRow[mapping.property];

          if (!res && mapping.property.includes('_accessObject_')) {
            // dig down into specified object(s)
            res = mapping.property
              .split('_accessObject_')
              .reduce((j, k) => j && j[k], dataRow);
          }

          return res;
        })
        .concat(aggregatedColumns.map((aColumn) => dataRow[aColumn.property])),
    );
    const headers = selectedColumns
      .map((mapping) => mapping.heading)
      .concat(aggregatedColumns.map((aColumn) => aColumn.heading));

    rows.unshift(headers);
    return rows;
  }

  static dateTime() {
    const d = new Date();
    const currentDateTime =
      d.getFullYear() +
      '-' +
      this.datePad(d.getMonth() + 1) +
      '-' + // getMonth() returns 0-11
      this.datePad(d.getDate()) +
      '-' +
      this.datePad(d.getHours()) +
      this.datePad(d.getMinutes()) +
      this.timeZone(d);

    return currentDateTime; // e.g. '2020-02-12-1451+0200'
  }

  static datePad(n: number) {
    return (Math.abs(n) < 10 ? '0' : '') + n;
  }

  static timeZone(date: Date) {
    const zoneSign = date.getTimezoneOffset() < 0 ? '+' : '-';
    return (
      zoneSign + this.datePad(Math.abs(date.getTimezoneOffset()) / 60) + '00'
    );
  }
}
